import {
  ActivityIcon,
  BinaryIcon,
  CableIcon,
  CloudOffIcon,
  ClipboardListIcon,
  CpuIcon,
  DollarSignIcon,
  FolderIcon,
  GaugeIcon,
  LayersIcon,
  MonitorIcon,
  NetworkIcon,
  PuzzleIcon,
  RadioIcon,
  SearchXIcon,
  SigmaIcon,
  UnplugIcon,
  WrenchIcon,
  ZapIcon,
} from 'lucide-react'

type LucideIcon = typeof ZapIcon

export interface IconItem {
  icon: LucideIcon
  title: string
  description: string
}

export const painPoints: IconItem[] = [
  {
    icon: PuzzleIcon,
    title: 'Sin Integración Clara',
    description:
      'Enseñás sensores aislados, PLC desconectado y SCADA como temas separados sin mostrar cómo hablan entre sí.',
  },
  {
    icon: SigmaIcon,
    title: 'Escalamiento que Confunde',
    description:
      'Tus alumnos no entienden por qué 4-20mA se convierte en un número, y menos cómo ese número controla un actuador.',
  },
  {
    icon: SearchXIcon,
    title: 'Diagnóstico a Ciegas',
    description:
      'Cuando algo no funciona en el laboratorio, gastás horas debuggeando sin un método sistemático.',
  },
  {
    icon: NetworkIcon,
    title: 'Protocolos sin Contexto',
    description: 'HART, Modbus, MQTT, Ethernet/IP. ¿Cuál usar? ¿Cuándo? Tus alumnos quedan con dudas.',
  },
  {
    icon: DollarSignIcon,
    title: 'Laboratorio Caro o Inexistente',
    description: 'O gastás $10,000 en un PLC moderno, o enseñás solo en teoría sin práctica real.',
  },
  {
    icon: ClipboardListIcon,
    title: 'Evaluación Sin Criterio',
    description:
      'Diseñar rúbricas técnicas que midan comprensión real (no solo memorización) es casi imposible.',
  },
  {
    icon: ActivityIcon,
    title: 'Mantenimiento Predictivo Desconocido',
    description:
      'La industria 4.0 ya existe. Tus alumnos salen sin saber que los datos anticipan fallas.',
  },
]

export interface IncludedItem {
  title: string
  description: string
}

export const includedInEveryVolume: IncludedItem[] = [
  {
    title: 'Recorrido completo del dato',
    description: 'Desde el sensor físico hasta la nube, paso a paso.',
  },
  {
    title: 'Protocolos paso a paso',
    description: '4-20mA, 0-10V, HART, Modbus RTU, Ethernet/IP y MQTT explicados con diagramas.',
  },
  {
    title: 'Escalamiento sin errores',
    description: 'Fórmulas verificadas, ejercicios resueltos y casos de calibración real.',
  },
  {
    title: 'Lógica de control práctica',
    description: 'PLC Ladder, PID, temporizadores, interlocks, alarmas y máquinas de estado.',
  },
  {
    title: 'HMI clara',
    description: 'Diseño de pantallas SCADA que los operadores entienden al instante.',
  },
  {
    title: 'Diagnóstico sistemático',
    description: 'Método de 10 pasos para encontrar fallas sin perder horas.',
  },
  {
    title: 'Mantenimiento predictivo',
    description: 'Anticipar fallos con datos reales y reducir paradas inesperadas.',
  },
  {
    title: 'Multi-marca',
    description: 'Siemens, Allen-Bradley, Schneider y Omron adaptadas a la misma lógica.',
  },
  {
    title: 'Laboratorios low-cost',
    description: 'Enseñá integración con $50 de material o sin hardware físico.',
  },
  {
    title: 'Casos de planta real',
    description: 'Fallas documentadas de minería, petróleo, energía e industria 4.0.',
  },
]

export interface Volume {
  icon: LucideIcon
  number: number
  title: string
  description: string
}

export const volumes: Volume[] = [
  {
    icon: CableIcon,
    number: 1,
    title: 'El ADN del Cableado Industrial',
    description:
      'Fundamentos de señal, blindaje, tierra, 4-20mA vs 0-10V, cableado correcto y prevención de interferencia electromagnética.',
  },
  {
    icon: BinaryIcon,
    number: 2,
    title: 'El Mapa de Memoria',
    description:
      'Escalamiento exacto de señales, conversión A/D, offset y calibración, fórmulas verificadas y ejercicios resueltos.',
  },
  {
    icon: CpuIcon,
    number: 3,
    title: 'Lógica de Control',
    description:
      'Programación PLC, control ON/OFF, PID, temporizadores, contadores, interlocks y máquinas de estado listas para aplicar.',
  },
  {
    icon: MonitorIcon,
    number: 4,
    title: 'Visualización HMI',
    description:
      'Diseño de pantallas SCADA, alarmas efectivas, mimic diagrams, tendencias, históricos y acceso por niveles de usuario.',
  },
  {
    icon: RadioIcon,
    number: 5,
    title: 'Protocolos Digitales',
    description:
      'HART, Modbus maestro-esclavo, Ethernet/IP, MQTT pub/sub, comparativas e implementación paso a paso.',
  },
  {
    icon: GaugeIcon,
    number: 6,
    title: 'Integración 4.0',
    description:
      'MQTT a cloud, Edge Computing, mantenimiento predictivo, analytics en tiempo real y casos de éxito en LATAM.',
  },
]

export interface Bonus {
  number: number
  title: string
  description: string
  image: string
}

export const bonuses: Bonus[] = [
  {
    number: 1,
    title: 'Plan Docente 30 Días',
    description:
      'Cronograma completo de 4 semanas: objetivos diarios, actividades, preguntas guía, mini evaluaciones y proyecto integrador final.',
    image: 'https://cdn.magicpatterns.com/uploads/dcTcRiz2aGDBKECGL5uiLK/ChatGPT_Image_31_ago_2026,_15_49_26.webp',
  },
  {
    number: 2,
    title: 'Checklist Maestro de Diagnóstico',
    description:
      'Verificación rápida en 10 pasos: cableado, señal, entrada, escalamiento, lógica y HMI. Detecta 99% de fallas en minutos.',
    image: 'https://cdn.magicpatterns.com/uploads/stVJXfZai8SdmP9VGjJfrn/ChatGPT_Image_31_ago_2026,_15_51_03.webp',
  },
  {
    number: 3,
    title: 'Banco de 60 Ejercicios Resueltos',
    description:
      'Problemas de dificultad progresiva: señales, escalamiento, control, HMI, protocolos e Industria 4.0, con soluciones paso a paso.',
    image: 'https://cdn.magicpatterns.com/uploads/2HfgzFMVWtFY6zSMm2RjEm/ChatGPT_Image_31_ago_2026,_15_52_21.webp',
  },
  {
    number: 4,
    title: 'Guía Rápida Multimarca',
    description:
      'Mapeo Siemens · Allen-Bradley · Schneider · Omron: direcciones, variables y escalamiento. El concepto es universal, no la marca.',
    image: 'https://cdn.magicpatterns.com/uploads/9bFLbV6yNzY44DNR2SNkTW/ChatGPT_Image_31_ago_2026,_15_53_43.webp',
  },
  {
    number: 5,
    title: 'Pack de Plantillas Imprimibles',
    description:
      '27 formularios listos: conexión, variables, alarmas, diagnóstico, checklists, rúbricas e históricos. Profesional, listo para usar.',
    image: 'https://cdn.magicpatterns.com/uploads/dW9GrUkyrbmsmhCeHYLBcK/ChatGPT_Image_31_ago_2026,_15_55_48.webp',
  },
  {
    number: 6,
    title: 'Manual de Proyectos Low-Cost',
    description:
      '10 proyectos integradores: tanque, temperatura, presión, sensores, cinta, secuencias, Modbus, MQTT y predictivo. Con y sin hardware.',
    image: 'https://cdn.magicpatterns.com/uploads/uxRyL49a5qWtbXwprvYhgw/ChatGPT_Image_31_ago_2026,_15_57_03.webp',
  },
]

export const offerIncludes: string[] = [
  'Volumen 1: El ADN del Cableado Industrial',
  'Volumen 2: El Mapa de Memoria (Escalamiento)',
  'Volumen 3: Lógica de Control',
  'Volumen 4: Visualización HMI',
  'Volumen 5: Protocolos Digitales',
  'Volumen 6: Integración 4.0',
  'Los 6 Bonos incluidos sin costo adicional',
  'Pago único. Acceso instantáneo desde hoy',
]

export interface Testimonial {
  quote: string
  initials: string
  name: string
  role: string
}

export const testimonials: Testimonial[] = [
  {
    quote:
      'Tenía alumnos que no entendían por qué 4-20mA se convertía en un número. Con el Volumen 2 ahora lo explico sin dudas. Además, el Bono 3 tiene 60 ejercicios resueltos que uso cada semana.',
    initials: 'JM',
    name: 'Jorge M.',
    role: 'Docente de Automatización — Argentina',
  },
  {
    quote:
      'El mayor miedo era que mis alumnos salieran sin saber integración real. El Plan de 30 Días (Bono 1) me organizó todo. Ahora tengo un programa coherente de sensor a nube.',
    initials: 'LP',
    name: 'Laura P.',
    role: 'Profesora de Procesos — Colombia',
  },
  {
    quote:
      'Enseño en un instituto con presupuesto bajo. Con el Manual de Proyectos Low-Cost (Bono 6) hago prácticas reales con $30 de material. No necesito PLCs caros.',
    initials: 'CS',
    name: 'Carlos S.',
    role: 'Instructor Técnico — Perú',
  },
]

export interface FaqItem {
  question: string
  answer: string
}

export const faqs: FaqItem[] = [
  {
    question: '¿Sirve si enseño solo en nivel técnico (no universitario)?',
    answer:
      'Sí. De hecho, está diseñado principalmente para ciclo superior técnico y especialidades. La profundidad se adapta a cada nivel, pero el enfoque es acción y aplicación práctica desde día uno.',
  },
  {
    question: '¿Cómo recibo el material?',
    answer:
      'Luego de tu pago, accedés instantáneamente a una plataforma con los 6 volúmenes + 6 bonos en PDF descargables. No hay envíos, no hay esperas. Entra en tu email un link para descargar todo.',
  },
  {
    question: '¿Cuánto tiempo lleva empezar a usar el Kit?',
    answer:
      '5 minutos. Bajás los PDFs, abrís el Volumen 1, leés la primera página y ya entendés el recorrido completo del dato. El Bono 1 (Plan de 30 Días) te da estructura para la próxima clase.',
  },
  {
    question: '¿Necesito software o equipos especiales?',
    answer:
      'No. Los volúmenes explican protocolos sin obligarte a usar una marca específica. El Bono 6 tiene 10 proyectos que funcionan con PLCs, con recursos low-cost, o sin hardware (solo conceptos). Vos elegís.',
  },
  {
    question: '¿Es real la garantía?',
    answer:
      '100% real. Comprás hoy, usás durante 7 días, y si no te convence, escribís un email pidiendo devolución. No hay preguntas, no hay análisis de por qué. Tu dinero vuelve a tu cuenta.',
  },
  {
    question: '¿Aplica para todos los países de habla hispana?',
    answer:
      'Sí. Están incluidos países, monedas locales y adaptaciones de terminología por región. Los casos de estudio cubren minería (Chile/Perú), petróleo (Colombia/México), energía (Argentina/España) e industria manufacturera en toda LATAM.',
  },
  {
    question: '¿Puedo usar el material con mis alumnos?',
    answer:
      'Completamente. Los volúmenes incluyen derechos de aula. Podés fotocopiar, proyectar, reasignar como tarea e imprimirlos. El material es tuyo para usar en tu contexto docente.',
  },
  {
    question: '¿Hay actualizaciones futuras?',
    answer:
      'Sí. Los volúmenes se actualizan anualmente (enero). Cualquier comprador recibe automáticamente las nuevas versiones sin pagar nada más. Garantía de contenido siempre vigente.',
  },
  {
    question: '¿Y si enseño en nivel superior/universitario?',
    answer:
      'El contenido también sirve, pero tené en cuenta que está diseñado con enfoque técnico-práctico. La teoría es rigurosa, pero el foco es "aplicar hoy en planta". Si buscás profundidad teórica pura (transformadas de Laplace, control avanzado), esto complementa pero no reemplaza tratados universitarios.',
  },
]

export const costOfInaction: IconItem[] = [
  {
    icon: LayersIcon,
    title: 'Sin ecosistema coherente',
    description:
      'Enseñanza fragmentada: sensor acá, PLC allá, SCADA desconectado. Los alumnos no ven la película completa.',
  },
  {
    icon: WrenchIcon,
    title: 'Sin método de diagnóstico',
    description: 'Cada falla en laboratorio es un misterio. Horas sin método, sin checklist, sin solución.',
  },
  {
    icon: ClipboardListIcon,
    title: 'Sin proyectos listos',
    description:
      'Diseñar proyectos integradores cada semestre consume energía que deberías dedicar a enseñar.',
  },
  {
    icon: FolderIcon,
    title: 'Sin casos reales',
    description: 'Clases abstractas sin ejemplos de fallas documentadas de planta.',
  },
  {
    icon: UnplugIcon,
    title: 'Sin laboratorios accesibles',
    description: 'O comprás un PLC de $3,000 o no hay laboratorio. Con el Bono 6 enseñás con $20-50.',
  },
  {
    icon: SigmaIcon,
    title: 'Sin evaluación profesional',
    description:
      'Rúbricas genéricas que no miden comprensión técnica real. El Bono 5 incluye 27 plantillas profesionales.',
  },
  {
    icon: CloudOffIcon,
    title: 'Sin Industria 4.0',
    description:
      'Tus alumnos salen sin saber mantenimiento predictivo, Edge Computing ni MQTT. La industria ya está ahí.',
  },
]

export const HERO_IMAGE =
  'https://cdn.magicpatterns.com/uploads/xkBGd44DjuQc3ibigJLQiU/ChatGPT_Image_31_ago_2026,_15_04_47-Photoroom_(1).webp'
